/** Hungarian copy of the guided first hour: one entry per server-tracked step (packages/shared onboarding.ts). */
import type { OnboardingStep, OnboardingGoal } from '@galaxis/shared';

export interface GuideStepCopy {
  title: string;
  /** Guide panel text */
  text: string;
  /** Short contextual hint shown inside the panel where the step happens */
  hint: string;
}

export const GUIDE_COPY: Record<OnboardingStep, GuideStepCopy> = {
  homeViewed: {
    title: 'Anyarendszer megtekintése',
    text: 'Ez a birodalmad szíve: innen termelsz, itt építesz hajót. Koppints rá a térképen a részletekhez.',
    hint: 'Koppints az anyarendszeredre a térképen.',
  },
  scoutLaunched: {
    title: 'Kutatóhajó küldése',
    text: 'A térképen minden csillagot és útvonalat látsz, de a nevük és a kincseik még rejtve vannak. Koppints egy szomszédos szürke rendszerre, és küldd oda a kutatóhajót: megérkezéskor kiderül a neve, és a hajó felméri.',
    hint: 'Koppints egy szomszédos, ismeretlen rendszerre, és indítsd el a kutatóhajót.',
  },
  firstSurvey: {
    title: 'Első rendszer felmérése',
    text: 'A név az érkezéskor derül ki; a felmérés után a lelőhelyek és a lakhatóság is látszik. A felmérés egy kis időbe telik.',
    hint: 'A kutatóhajó megérkezés után felméri a rendszert — várd meg a végét.',
  },
  depositFound: {
    title: 'Lelőhely keresése',
    text: 'A felmért rendszerekben fém- és energialelőhelyeket találsz. Keress olyat, ami nem az anyarendszeredben van — a kutatóhajóddal újabb rendszereket is felmérhetsz.',
    hint: 'Keress felmért rendszert lelőhellyel (⚙️ fém, ⚡ energia). Ha nincs, küldd tovább a kutatóhajót.',
  },
  outpostBuilt: {
    title: 'Előőrs építése',
    text: 'Az előőrs a tiéddé teszi a rendszert, és állomás csak a saját rendszeredben épülhet. Küldd oda az építőhajót, majd építs előőrsöt (fémbe kerül).',
    hint: 'Küldd ide az építőhajót, és építs előőrsöt a rendszer elfoglalásához.',
  },
  firstUpgrade: {
    title: 'Bánya építése',
    text: 'A bánya vagy energiaállomás a lelőhelyből folyamatos termelést csinál. Válaszd a saját rendszeredet, és építs állomást a lelőhelyre.',
    hint: 'Építs bányát (fém) vagy erőművet (energia) a lelőhelyre.',
  },
  productionSeen: {
    title: 'Termelés megtekintése',
    text: 'Nézd meg a Gazdaság fülön, honnan mennyi fém és energia jön, és mibe kerül a fenntartás. A termelés akkor is megy, ha nem vagy bent.',
    hint: 'Itt látod, melyik állomás mennyit termel. A termelés a távollétedben is folytatódik.',
  },
  energySeen: {
    title: 'Energiamérleg',
    text: 'Az energia nem csak tárolható: a működő gazdaságnak folyamatosan kell is. Ha a nettó energia negatív, a bányák lassabban termelnek. Építs időben erőművet.',
    hint: 'Nettó energia = termelés − fenntartás. Ha negatív, csökken a hatékonyság: építs energiaállomást.',
  },
  civTrace: {
    title: 'Egy másik civilizáció nyoma',
    text: 'A galaxis közös: máshol is laknak. Amint a hajód egy másik civilizáció rendszerébe érkezik, megismered őt, és felveheted vele a kapcsolatot a Diplomácia fülön. Fedezz fel tovább, amíg ráakadsz egy nyomra.',
    hint: 'Még nem találtál más civilizációt. Fedezz fel tovább — amint ráakadsz, itt jelenik meg.',
  },
  goalChosen: {
    title: 'Saját cél választása',
    text: 'Az alapok megvannak. Mi legyen a következő nagy célod? Később bármikor átgondolhatod.',
    hint: 'Válassz célt az útmutatóban.',
  },
};

export const GOAL_COPY: Record<OnboardingGoal, { label: string; text: string; tab: 'galaxy' | 'empire' | 'research' | 'diplomacy' }> = {
  expand: { label: 'Terjeszkedés', text: 'Több rendszer, nagyobb birodalom.', tab: 'galaxy' },
  economy: { label: 'Gazdaság', text: 'Erős termelés, fejlesztett állomások.', tab: 'empire' },
  research: { label: 'Kutatás', text: 'Technológiai előny a többiek felett.', tab: 'research' },
  diplomacy: { label: 'Diplomácia', text: 'Szövetségek és kereskedelem.', tab: 'diplomacy' },
};
